import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import DownloadIcon from 'mdi-react/DownloadIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import LockResetIcon from 'mdi-react/LockResetIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import TagPlusIcon from 'mdi-react/TagPlusIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import { splitEvery } from 'ramda';
import { Parser } from 'json2csv';
import exportToExcelFile from '../../../util/xlsx';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import Pagination from '../../../shared/components/pagination/Pagination';
import t, { partial } from '../../../util/translation/translation';
import { capitalizeFirstLetter } from '../../../util/functions';
import useModal from '../../../shared/hooks/useModal';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import FormCard from '../../../shared/components/custom/FormCard';
import useConfig from '../../../util/useConfig';
import { formatDate } from '../../../util/date-and-time';

const p = partial('UsersTab');
const s = partial('shared');

// Phone searches get typed with +, spaces, dashes or parens (e.g.
// "+46 70-123 45 67"), which won't match the digits-only stored number.
// Only normalize when the whole query already looks like a phone number -
// a name search (letters) is left untouched.
const normalizeSearchQuery = (text) => {
  const trimmed = (text || '').trim();
  if (!/^[+\d\s().-]+$/.test(trimmed)) {
    return text;
  }
  return trimmed.replace(/[^\d]/g, '');
};

const UsersTab = () => {
  const config = useConfig();
  const history = useHistory();
  const [handleModal, visible, deleteId, comment, setComment] = useModal();
  const [usersPaginated, setUsersPaginated] = useState({ total: 0, users: [] });
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 0,
  });
  const [sort, setSort] = useState({
    field: 'phoneNumber',
    direction: 'asc',
  });

  const getUsers = async () => {
    setLoading(true);
    const { total, users } = await Api.users.getTeamUsers(
      pagination.page,
      pagination.pageSize,
      { [sort.field]: sort.direction },
      normalizeSearchQuery(searchText),
      config
    );
    try {
      const { data: usersTiers } = await Api.tiers.userTiersByCloudIDs(
        users.map((user) => user.userCloudId),
        config
      );
      setLoading(false);
      setUsersPaginated({
        total,
        users: users.map((user) => ({
          ...user,
          tiers: usersTiers.find((tier) => tier.userID === user.userCloudId),
        })),
      });
    } catch (e) {
      setLoading(false);
    }
  };

  // Emails the user a link to choose a new password themselves (the user
  // service's reset email), rather than a new password in plain text
  const sendRestPassword = async (user) => {
    if (!user?.email) {
      toast.error(p('userHasNoEmail'));
      return;
    }
    setLoading(true);
    try {
      await Api.users.requestPasswordReset(user.email);
      toast.success(p('passwordResetSent', { email: user.email }));
    } catch (err) {
      toast.error(s('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pagination.page !== 0) {
      getUsers();
    }
  }, [pagination.page, pagination.pageSize, sort.field, sort.direction]);

  const onChangePage = (page) => {
    setPagination({
      page: page.currentPage,
      pageSize: page.pageSize,
    });
  };

  const handleSearchInput = (e) => {
    setSearchText(e.target.value);
  };

  const search = (e) => {
    if (e.key === 'Enter') {
      getUsers();
    }
  };

  const handleSort = (field) => {
    const getReversedSortDirection = (direction) => {
      return direction === 'asc' ? 'desc' : 'asc';
    };
    setSort((prev) => ({
      field,
      direction:
        prev.field === field ? getReversedSortDirection(prev.direction) : 'asc',
    }));
  };

  const renderSort = (cellOrder) => {
    if (sort.field !== cellOrder) {
      return undefined;
    }
    return sort.direction === 'asc' ? (
      <ArrowUpIcon size={16} />
    ) : (
      <ArrowDownIcon size={16} />
    );
  };

  const deleteUser = async () => {
    setLoading(true);
    try {
      await Api.users.deleteTeamUser(config.cloudIdName, deleteId, comment);
      toast.success(p('deleteUserSuccess'));
    } catch (err) {
      toast.error(p('deleteUserFailed'));
    }
    handleModal();
    getUsers();
  };
  const handleExport = async () => {
    setLoading(true);
    try {
      const { users: fetchedUsers } = await Api.users.getTeamUsers(
        1,
        100000,
        { phoneNumber: 'asc' },
        '',
        config
      );
      //const chunkedUsers = splitEvery(200, fetchedUsers);

      const mappedUsers = fetchedUsers.map((user) => {
        const allRoles = `${
          user.teams?.[0]?.roles
            ?.map((role) => role.name)
            .slice(0, 3)
            .join(', ') || '-'
        }${user.teams?.[0]?.roles?.length > 3 ? '...' : ''}`;
        return {
          ...user,
          dateOfBirth:
            !user.dateOfBirth || user.dateOfBirth === '0000-00-00'
              ? '-'
              : user.dateOfBirth,
          tier: '-',
          roles: allRoles,
          createdAt: user?.createdAt ? formatDate(user?.createdAt) : '-',
          lastLoggedInAt: user?.lastLoggedInAt
            ? formatDate(user?.lastLoggedInAt)
            : '-',
        };
      });
      const fields = [
        { label: t('AddEmployee.firstName'), value: 'firstname' },
        { label: t('AddEmployee.lastName'), value: 'lastname' },
        { label: s('phone'), value: 'username' },
        { label: s('email'), value: 'email' },
        { label: capitalizeFirstLetter(t('UserTiers.tier')), value: 'tier' },
        { label: s('created'), value: 'createdAt' },
        { label: s('lastLoggedInAt'), value: 'lastLoggedInAt' },
        { label: s('roles'), value: 'roles' },
        { label: s('dateOfBirth'), value: 'dateOfBirth' },
        { label: 'ID', value: 'userCloudId' },
      ];
      const json2csvParser = new Parser({ fields });
      const csv = json2csvParser.parse(mappedUsers);
      fileDownload(`\uFEFF${csv}`, `${p('exportUsers')}.csv`);
    } catch (e) {
      toast.error(s('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportXLSX = async () => {
    setLoading(true);
    try {
      const { users: fetchedUsers } = await Api.users.getTeamUsers(
        1,
        100000,
        { phoneNumber: 'asc' },
        '',
        config
      );
      const chunkedUsers = splitEvery(200, fetchedUsers);
      /*const { data: usersTiers } = await Api.tiers.userTiersByCloudIDs(
        chunkedUsers.map((user) => user.userCloudId),
        config
      );*/
      const data = fetchedUsers.map((user) => {
        const allRoles = `${
          user.teams?.[0]?.roles
            ?.map((role) => role.name)
            .slice(0, 3)
            .join(', ') || '-'
        }${user.teams?.[0]?.roles?.length > 3 ? '...' : ''}`;

        const result = {};
        result[`${t('AddEmployee.firstName')}`] = user?.firstname;
        result[`${t('AddEmployee.lastName')}`] = user?.lastname;
        result[`${s('phone')}`] = user?.username;
        result[`${s('email')}`] = user?.email;
        result[`${capitalizeFirstLetter(t('UserTiers.tier'))}`] = '-';
        result[`${s('created')}`] = user?.createdAt
          ? formatDate(user?.createdAt)
          : '-';
        result[`${s('lastLoggedInAt')}`] = user?.lastLoggedInAt
          ? formatDate(user?.lastLoggedInAt)
          : '-';
        result[`${s('roles')}`] = allRoles;
        result[`${s('dateOfBirth')}`] =
          !user.dateOfBirth || user.dateOfBirth === '0000-00-00'
            ? '-'
            : user.dateOfBirth;
        result[`${'ID'}`] = user?.userCloudId;

        return result;
      });

      exportToExcelFile(data, `${p('exportUsers')}`);
    } catch (e) {
      toast.error(s('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportTerms = async () => {
    setLoading(true);
    try {
      const { data: results } = await Api.terms.getUserTermApprovals();
      const mappedResults = results.map((item) => ({
        ...item,
        SMS: item.SMS ? s('yes') : s('no'),
        EMAIL: item.EMAIL ? s('yes') : s('no'),
        PUSH: item.PUSH ? s('yes') : s('no'),
        GENERAL: item.GENERAL ? s('yes') : s('no'),
      }));
      const fields = [
        { label: 'ID', value: 'userCloudId' },
        { label: t('AddEmployee.firstName'), value: 'firstname' },
        { label: t('AddEmployee.lastName'), value: 'lastname' },
        { label: s('phone'), value: 'phoneNumber' },
        { label: s('email'), value: 'email' },
        { label: t('settingsTerms.termName-SMS'), value: 'SMS' },
        { label: t('settingsTerms.termName-EMAIL'), value: 'EMAIL' },
        { label: t('settingsTerms.termName-PUSH'), value: 'PUSH' },
        { label: t('settingsTerms.termName-GENERAL'), value: 'GENERAL' },
      ];
      const json2csvParser = new Parser({ fields });
      const csv = json2csvParser.parse(mappedResults);
      fileDownload(`\uFEFF${csv}`, `${p('exportTermApprovals')}.csv`);
    } catch {
      toast.error(s('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type={p('userDeleteType')}
        modalAction={deleteUser}
        canComment
        comment={comment}
        onCommentChange={setComment}
      />
      <Loading loading={loading} />
      <FormCard
        title={s('users')}
        actions={
          <>
            <div
              className="d-none d-md-flex align-items-center"
              style={{ gap: 10 }}
            >
              <div className="form noWrap">
                <div
                  className="form__form-group products-list__search"
                  style={{ minWidth: 220 }}
                >
                  <MagnifyIcon
                    role="button"
                    onClick={getUsers}
                    style={{ left: 10, right: 'auto', cursor: 'pointer' }}
                  />
                  <input
                    placeholder={p('searchForUser')}
                    value={searchText}
                    onChange={handleSearchInput}
                    onKeyPress={search}
                    name="search"
                    style={{ paddingLeft: 30, height: 32, borderRadius: 5 }}
                  />
                </div>
              </div>
              <UncontrolledDropdown>
                <DropdownToggle
                  className="icon icon--right mb-0"
                  outline
                  size="sm"
                >
                  <DownloadIcon /> {s('export')} <ChevronDownIcon />
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem key="exp2" onClick={handleExportXLSX}>
                    {p('exportUsers')} (XLSX)
                  </DropdownItem>
                  <DropdownItem key="exp1" onClick={handleExport}>
                    {p('exportUsers')} (CSV)
                  </DropdownItem>
                  <DropdownItem key="exp3" onClick={handleExportTerms}>
                    {p('exportTermApprovals')} (CSV)
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <Link to="/users/create">
                <Button size="sm" color="primary" className="mb-0">
                  {p('createUser')}
                </Button>
              </Link>
            </div>
            <div
              className="d-flex d-md-none align-items-center"
              style={{ gap: 8 }}
            >
              <div className="form noWrap">
                <div className="form__form-group products-list__search">
                  <input
                    placeholder={p('searchForUser')}
                    value={searchText}
                    onChange={handleSearchInput}
                    onKeyPress={search}
                    name="search"
                  />
                </div>
              </div>
              <UncontrolledDropdown>
                <DropdownToggle
                  tag="button"
                  type="button"
                  className="square-icon-btn"
                  caret={false}
                >
                  <DotsHorizontalIcon />
                </DropdownToggle>
                <DropdownMenu right className="dropdown-menu--modern">
                  <DropdownItem onClick={handleExportXLSX}>
                    {p('exportUsers')} (XLSX)
                  </DropdownItem>
                  <DropdownItem onClick={handleExport}>
                    {p('exportUsers')} (CSV)
                  </DropdownItem>
                  <DropdownItem onClick={handleExportTerms}>
                    {p('exportTermApprovals')} (CSV)
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem tag={Link} to="/users/create">
                    {p('createUser')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </>
        }
        bodyClassName="form-card__body--flush"
        className="mb-3"
      >
        <Table responsive striped className="table--roomy table--stack">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort('firstname')}>
                {s('name')} {renderSort('firstname')}
              </th>
              <th>
                <span
                  className="sortable"
                  onClick={() => handleSort('phoneNumber')}
                >
                  {s('phonenumber')} {renderSort('phoneNumber')}
                </span>
                {' / '}
                <span className="sortable" onClick={() => handleSort('email')}>
                  {s('email')} {renderSort('email')}
                </span>
              </th>
              <th>{capitalizeFirstLetter(t('UserTiers.tier'))}</th>
              <th>
                <span
                  className="sortable"
                  onClick={() => handleSort('createdAt')}
                >
                  {s('created')} {renderSort('createdAt')}
                </span>
                {' / '}
                <span
                  className="sortable"
                  onClick={() => handleSort('lastLoggedInAt')}
                >
                  {s('lastLoggedInAt')} {renderSort('lastLoggedInAt')}
                </span>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!loading && usersPaginated.users.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted">
                  {p('noUsersFound')}
                </td>
              </tr>
            )}
            {usersPaginated.users.map((user) => (
              <tr key={`user-${user.userCloudId}`}>
                <td className="table-stack__primary">
                  <div>
                    {[user?.firstname, user?.lastname]
                      .filter(Boolean)
                      .join(' ') || '-'}
                  </div>
                  <div>
                    {user.teams?.[0]?.roles?.slice(0, 3).map((role) => (
                      <Badge
                        key={role.id ?? role.name}
                        pill
                        color="secondary"
                        className="mr-1"
                      >
                        {role.name}
                      </Badge>
                    ))}
                    {user.teams?.[0]?.roles?.length > 3 && (
                      <Badge pill color="secondary">
                        +{user.teams[0].roles.length - 3}
                      </Badge>
                    )}
                  </div>
                </td>
                <td data-label={`${s('phonenumber')} / ${s('email')}`}>
                  <div>
                    <Link to={`/users/edit/${user.userCloudId}`}>
                      {user?.phoneNumber ? (
                        `${user?.flag ?? ''} ${user.phoneNumber}`
                      ) : (
                        // Signed up with email only: tickets, memberships and
                        // company seats are matched on the phone
                        <Badge pill color="warning">
                          {p('noPhoneConnected')}
                        </Badge>
                      )}
                    </Link>
                  </div>
                  <small className="text-muted">{user?.email ?? '-'}</small>
                </td>
                <td data-label={capitalizeFirstLetter(t('UserTiers.tier'))}>
                  {user?.tiers?.allTierNames?.length > 0 && (
                    <Link to={`/users/edit/${user.userCloudId}?tab=3`}>
                      {user?.tiers?.allTierNames?.slice(0, 3)?.join(', ')}
                    </Link>
                  )}
                </td>
                <td data-label={`${s('created')} / ${s('lastLoggedInAt')}`}>
                  <div>
                    {user?.createdAt ? formatDate(user?.createdAt) : '-'}
                  </div>
                  <small className="text-muted">
                    {user?.lastLoggedInAt
                      ? formatDate(user?.lastLoggedInAt)
                      : '-'}
                  </small>
                </td>
                <td className="table-stack__actions">
                  <div className="icon-actions">
                    {!user?.tiers?.allTierNames?.length && (
                      <SquareIconButton
                        icon={<TagPlusIcon />}
                        onClick={() =>
                          history.push(`/users/edit/${user.userCloudId}?tab=3`)
                        }
                        title={p('addTier')}
                        id={`add-tier-${user.userCloudId}`}
                      />
                    )}
                    <SquareIconButton
                      icon={<LockResetIcon />}
                      onClick={() => sendRestPassword(user)}
                      title={p('resetPassword')}
                      id={`reset-password-${user.userCloudId}`}
                    />
                    <SquareIconButton
                      icon={<DeleteOutlineIcon />}
                      onClick={() => handleModal(user.userCloudId)}
                      title={s('delete')}
                      tone="danger"
                      id={`delete-user-${user.userCloudId}`}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </FormCard>
      <Pagination
        rowsPerPage={[100, 250, 500]}
        items={usersPaginated.total}
        onChangePage={onChangePage}
      />
    </>
  );
};

export default UsersTab;
